import React from 'react'
import { connect } from 'react-redux'
import filter from 'lodash/filter'
import find from 'lodash/find'
import sortBy from 'lodash/sortBy'
import Icon from '../../misc/Icons'
import { getQuestionBodyLabel } from '../../../utils/getQuestionBodyLabel'
import { formatDate } from '../../../utils/formatDate'

const MAX_CHILDREN = 8

// Resolve a list of folder ids to {name} entries; skip ids we can't find.
function resolveAncestors(ancestorIds, allFolders) {
  if (!Array.isArray(ancestorIds) || ancestorIds.length === 0) return []
  return ancestorIds
    .map((id) => find(allFolders, { id }))
    .filter(Boolean)
    .map((f) => ({ id: f.id, name: f.name }))
}

function SearchPreviewFolder({
  hit, storeFolder, subfolders, sets, questions, ancestorPath,
}) {
  const folder = storeFolder || hit
  const setCount = sets.length
  const questionCount = questions.length
  const subfolderCount = subfolders.length

  // First N child rows — subfolders first, then sets, then questions.
  const children = []
  for (let i = 0; i < subfolders.length && children.length < MAX_CHILDREN; i += 1) {
    children.push({ kind: 'folder', item: subfolders[i] })
  }
  for (let i = 0; i < sets.length && children.length < MAX_CHILDREN; i += 1) {
    children.push({ kind: 'set', item: sets[i] })
  }
  for (let i = 0; i < questions.length && children.length < MAX_CHILDREN; i += 1) {
    children.push({ kind: 'question', item: questions[i] })
  }

  const isEmpty = setCount === 0 && questionCount === 0 && subfolderCount === 0
  const editedAt = folder && folder.clientModified

  return (
    <div className='searchPreviewPane-body searchPreviewFolder'>
      <div className='searchPreviewPane-header'>
        <div className='searchPreviewPane-header-icon'>
          <Icon name={folder && folder.readOnly ? 'folder-isbe' : 'folder'} />
        </div>
        <div className='searchPreviewPane-header-text'>
          <div className='searchPreviewPane-header-title'>
            {folder && folder.name}
          </div>
          {ancestorPath.length > 0 && (
            <div className='searchPreviewFolder-breadcrumb'>
              {ancestorPath.map((node, idx) => (
                <React.Fragment key={node.id}>
                  {idx > 0 && <Icon name='chevron-right' />}
                  <span className='searchPreviewFolder-breadcrumb-name'>{node.name}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className='searchPreviewFolder-summary'>
        <div className='searchPreviewFolder-summary-chip'>
          <Icon name='folder' />
          <span>{`${subfolderCount} subfolder${subfolderCount === 1 ? '' : 's'}`}</span>
        </div>
        <div className='searchPreviewFolder-summary-chip'>
          <Icon name='search-set' />
          <span>{`${setCount} set${setCount === 1 ? '' : 's'}`}</span>
        </div>
        <div className='searchPreviewFolder-summary-chip'>
          <Icon name='search-question' />
          <span>{`${questionCount} question${questionCount === 1 ? '' : 's'}`}</span>
        </div>
      </div>

      {isEmpty ? (
        <div className='searchPreviewFolder-empty'>
          <Icon name='folder' />
          <span>This folder is empty.</span>
        </div>
      ) : (
        <ul className='searchPreviewFolder-list'>
          {children.map((row) => {
            if (row.kind === 'folder') {
              return (
                <li key={`f-${row.item.id}`} className='searchPreviewFolder-list-row'>
                  <Icon name={row.item.readOnly ? 'folder-isbe' : 'folder'} />
                  <span>{row.item.name}</span>
                </li>
              )
            }
            if (row.kind === 'set') {
              const qCount = Array.isArray(row.item.questions) ? row.item.questions.length : 0
              return (
                <li key={`s-${row.item.id}`} className='searchPreviewFolder-list-row'>
                  <Icon name='search-set' questionCount={qCount} />
                  <span>{row.item.name || 'Untitled Set'}</span>
                </li>
              )
            }
            return (
              <li key={`q-${row.item.id}`} className='searchPreviewFolder-list-row'>
                <Icon name='search-question' />
                <span>{getQuestionBodyLabel(row.item)}</span>
              </li>
            )
          })}
        </ul>
      )}
      <div className='searchPreviewPane-meta'>
        <span className='searchPreviewPane-meta-kind'>Folder</span>
        {editedAt && (
          <span className='searchPreviewPane-meta-edited'>
            {'Edited '}
            {formatDate(editedAt)}
          </span>
        )}
      </div>
    </div>
  )
}

function mapStateToProps(state, ownProps) {
  const id = ownProps.hit && ownProps.hit.id
  if (!id) {
    return {
      storeFolder: null, subfolders: [], sets: [], questions: [], ancestorPath: [],
    }
  }
  const storeFolder = find(state.folders, { id })
  const hit = ownProps.hit
  const ancestorIds = (storeFolder && storeFolder.ancestors) || (hit && hit.ancestors) || []

  const subfolders = sortBy(
    filter(state.folders, (f) => f && f.parent === id && !f.archived),
    'name',
  )
  const sets = sortBy(
    filter(state.sets, (s) => s && s.folder === id && !s.archived),
    'name',
  )
  const questions = sortBy(
    filter(state.questions, (q) => q && q.folder === id && !q.archived),
    'body',
  )
  const ancestorPath = resolveAncestors(ancestorIds, state.folders)
  return {
    storeFolder, subfolders, sets, questions, ancestorPath,
  }
}

export default connect(mapStateToProps)(SearchPreviewFolder)
